import * as API from './api'
import * as Store from './store'
import DraftManagerService from './service/DraftManager.service'
import sessionService from '@/app-domains/user/session/session.service'
import ToastService from '@/components/Toast/Toast.service'
import store from '@/core/store'

class WritingService {
  async setMessageText (text) {
    DraftManagerService.updateOrCreateDraft('reply', {
      slateContent: this.buildSlateContentFromText(text),
      messageText: text.split('\n').map(text => text.length > 0 ? `<p>${text}</p>` : '<br/>').join(''),
      messageHTML: text.split('\n').map(text => text.length > 0 ? `<p>${text}</p>` : '<br/>').join('')
    })
  }

  async setSubject (subject, type = 'standalone') {
    await DraftManagerService.updateOrCreateDraft(type, {
      subject
    })
  }

  async setFrom (from, type = 'standalone') {
    await DraftManagerService.updateOrCreateDraft(type, {
      from
    })
  }

  async setTosDirty (tosDirty, type = 'standalone') {
    await DraftManagerService.updateOrCreateDraft(type, {
      tosDirty,
    })
  }

  async setTosFromBox (tos) {
    await DraftManagerService.updateOrCreateDraft('standalone', {
      tos: Array.from(new Set(tos)),
    })
  }

  async setFromBox ({ boxName, boxId }) {
    let response = { participants: [], participantsWithNames: [] }
    if (boxId) {
      response = await API.getParticipantsInBox(boxId)
    }
    await DraftManagerService.updateOrCreateDraft('standalone', {
      from: boxName,
      boxId,
    })

    // Build a deduped list of {email, name} objects. Use participantsWithNames when the
    // backend supplies it; otherwise fall back to the legacy string-array shape.
    let participants = []
    if (Array.isArray(response.participantsWithNames) && response.participantsWithNames.length > 0) {
      const seen = new Set()
      participants = response.participantsWithNames.filter((p) => {
        if (!p?.email || seen.has(p.email)) return false
        seen.add(p.email)
        return true
      })
    } else if (Array.isArray(response.participants) && response.participants.length > 0) {
      participants = Array.from(new Set(response.participants)).map((email) => ({ email, name: null }))
    }

    if (participants.length > 0) {
      store.commit('DRAFT_MESSAGE:RELATED_CONTACTS_FROM_BOX', participants)
    }
  }

  async setMessageBody (type, slateContent, messageText, messageHTML) {
    messageText = messageText.replace(/\n/g, '<br/>')
    messageHTML = messageHTML.replace(/\n/g, '<br/>')

    await DraftManagerService.updateOrCreateDraft('standalone', {
      slateContent,
      messageText,
      messageHTML
    })
  }

  async setMessageEditedInExpandedMode (type = 'reply') {
    await DraftManagerService.updateOrCreateDraft(type, {
      shouldEditOnModal: true
    })
  }
  
  setDraftReplyBaseData (message, thread) {
    const defaultDomain = sessionService.getMe().me.defaultdomain
    DraftManagerService.setDraftType('reply')
    // We need to reset and create a new draft if draft is not setup and reply not found
    DraftManagerService.unSetActiveDraft()
    // Inject base values extracted from the parent message
    const temporalValues = DraftManagerService.setCurrentDraftValuesBeforeCreationFromParentMessage(
      message,
      thread,
      defaultDomain,
      'reply'
    )
    return temporalValues
  }

  setForwardBaseData (message, thread) {
    const defaultDomain = sessionService.getMe().me.defaultdomain
    DraftManagerService.setDraftType('forward')
    // We need to reset and create a new draft if draft is not setup and reply not found
    DraftManagerService.unSetActiveDraft()
    // Inject base values extracted from the parent message
    const temporalValues = DraftManagerService.setCurrentDraftValuesBeforeCreationFromParentMessage(
      message,
      thread,
      defaultDomain,
      'forward'
    )
    return temporalValues
  }

  buildSlateContentFromText (text) {
    return [
      {
        type: 'paragraph',
        children: [
          { text }
        ],
      }
    ]
  }

  async addToAddress (address, type = 'tos') {
    await DraftManagerService.createDraftIfNoActiveOneExists()
    if (this.checkIfAddressIsAlreadyStored(address)) {
      ToastService.show({
        type: 'error',
        title: 'Cannot add address',
        message: 'Address is already present'
      })
      return false
    }
    if (type === 'tos') {
      Store.addToAddressToStore(address)
    } else if (type === 'ccs') {
      Store.addCCAddressToStore(address)
    } else if (type === 'bccs') {
      Store.addBCCAddressToStore(address)
    }
    await DraftManagerService.updateDraftMessage()
    return true
  }

  // Was: `currentDraft?.tos?.indexOf(address) !== -1 || ...`
  //
  // That failed CLOSED. When any of the three lists was missing -- draft not
  // created yet, still being created, list absent from the API payload, or the
  // type-guard in getCurrentDraftFromList() returning undefined -- the optional
  // chain produced `undefined`, and `undefined !== -1` is true. The caller read
  // that as "already present", showed "Address is already present", and threw
  // the address away. Adding a recipient to a fresh forward silently did
  // nothing, so `tos` stayed empty and the Send button never enabled.
  //
  // Now it fails OPEN: only a list that genuinely contains the address blocks
  // it. Adding a duplicate is harmless and visible; dropping a recipient is
  // neither.
  checkIfAddressIsAlreadyStored (address) {
    const currentDraft = DraftManagerService.getStoredDraftMessage()
    const contains = (list) => Array.isArray(list) && list.includes(address)
    return contains(currentDraft?.tos) ||
      contains(currentDraft?.ccs) ||
      contains(currentDraft?.bccs)
  }

  async addToBox (boxName) {
    await DraftManagerService.createDraftIfNoActiveOneExists()
    this.setFrom(boxName)
    Store.addToBoxNameToStore(boxName)
    return await DraftManagerService.updateDraftMessage()
  }

  async removeToAddress (address) {
    Store.removeToAddressFromDraft(address)
    return await DraftManagerService.updateDraftMessage()
  }

  async removeCCAddress (address) {
    Store.removeCCAddressFromDraft(address)
    return await DraftManagerService.updateDraftMessage()
  }

  async removeBCCAddress (address) {
    Store.removeBCCAddressFromDraft(address)
    return await DraftManagerService.updateDraftMessage()
  }

  // Pending to refactor, needs to use DraftManagerService to retrieve draftId and upload attachment
  async uploadAttachment (file, fileContent) {
    try {
      ToastService.show({
        type: 'info',
        title: 'Uploading',
        message: 'Uploading attachment ... ',
      })
      await DraftManagerService.createDraftIfNoActiveOneExists()

      const data = {
        filename: file.name.replace(/\s/g, '_'),
        file: fileContent.split(',')[1],
        contentType: file.type
      }

      Store.storeNewAttachment({ filename: data.filename, filesize: file.size, isUploading: true })

      const draftId = Store.getCurrentDraftFromList().id
      const response = await API.uploadStandaloneMessageAttachment(draftId, data)

      Store.updateAttachment(data.filename, { filename: data.filename, filesize: file.size, id: response.composedid, url: response.url || response.uri })
      ToastService.show({
        type: 'success',
        title: 'Success!',
        message: 'Attachment uploaded',
      })
    } catch (e) {
      ToastService.show({
        type: 'error',
        title: 'Error',
        message: 'There was an error meanwhile uploading attachment',
      })
    }
  }

  // Pending to refactor, needs to use DraftManagerService to retrieve draftId and upload attachment
  async deleteAttachment (draftId, attachmentId) {
    ToastService.show({
      type: 'info',
      message: 'Deleting attachment ...',
    })
    await API.deleteStandaloneMessageAttachment(draftId, attachmentId)
    ToastService.show({
      type: 'success',
      message: 'Attachment deleted',
    })
    Store.deleteAttachmentFromStore(attachmentId)
  }

  isMessageReadyToBeSent () {
    const message = Store.getCurrentDraft()
    if (!message) {
      return false
    }
    const fieldsToCheck = [
      'from',
      ['tos', 'toBox'],
    ]

    for (const field of fieldsToCheck) {
      if (Array.isArray(field)) {
        const response = field.some(fieldItem => message[fieldItem] && message[fieldItem].length !== 0)
        if (!response) { return false }
      } else if (!message[field] || message[field].length === 0) {
        return false
      }
    }
    return true
  }

  buildSubject (subject, type) {
    const types = {
      reply: {
        footprint: 'Re',
        regexp: /Re\s{0,4}:/ig
      },
      forward: {
        footprint: 'Fwd',
        regexp: /Fwd\s{0,4}:/ig
      }
    }
    const subjectType = types[type.toLowerCase()]
    if (!subject) return ''
    // eslint-disable-next-line
    const regex = new RegExp(`^(?:(?:${subjectType.footprint}\\s{0,5}:)\\s?)+`, 'gi')
    
    if (subject.match(regex)?.length > 0) {
      subject = subject.replace(regex, `${subjectType.footprint}: `)
      return subject
    }
    return `${subjectType.footprint}: ${subject}`
  }

  async uploadEmbeddedImage (data) {
    return await API.uploadEmbeddedImage({
      encodedData: data
    })
  }

  checkIfMaxAddressesWillBeReached () {
    return false
  }
}

export default new WritingService()
